import { useMemo } from "react";

import { SidebarMenu, SidebarMenuItem } from "components/organisms/SideBar";
import { Dashboard as DashboardTemplate } from "components/templates/Dashboard";
import { useAuth, usePermission } from "hooks";
import NotFoundPage from "pages/NotFound";
import { Route, Routes } from "react-router-dom";

import { DashboardRoutes, DashboardSuspenseWrapper } from "./dashboard";
import { RequireAuth } from "./private";
import { PublicRoutes, PublicSuspenseWrapper } from "./public";
import { SidebarMenuRoutes } from "./sidebar";

const AppRoutes = () => {
  const { isInitializing, data } = useAuth();
  const { canAccess } = usePermission();

  const sidebarMenuBasedOnPermissions = useMemo(() => {
    if (!data) return [] as Array<SidebarMenu>;
    return SidebarMenuRoutes.reduce((prev: Array<SidebarMenu>, next) => {
      const filteredChildren: Array<SidebarMenuItem> = [];
      for (const c of next.children) {
        const { permissions, ...innerChild } = c;
        if (permissions && permissions.some(canAccess)) {
          filteredChildren.push(innerChild);
        }
      }
      return filteredChildren.length > 0
        ? [...prev, { ...next, children: filteredChildren }]
        : prev;
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (isInitializing) return null;

  return (
    <Routes>
      <Route
        element={
          <RequireAuth>
            <DashboardTemplate
              accountInfo={
                data
                  ? {
                      name: data.fullName,
                      role: data.isSuperAdmin ? "Super Admin" : "",
                    }
                  : undefined
              }
              sidebarMenu={sidebarMenuBasedOnPermissions}
            />
          </RequireAuth>
        }
      >
        {DashboardRoutes.map(({ useSuspense, element, ...innerProps }) => {
          return (
            <Route
              key={innerProps.path}
              {...innerProps}
              element={
                useSuspense ? (
                  <DashboardSuspenseWrapper key={innerProps.path}>
                    {element}
                  </DashboardSuspenseWrapper>
                ) : (
                  element
                )
              }
            />
          );
        })}
      </Route>
      {PublicRoutes.map(({ useSuspense, element, ...innerProps }) => (
        <Route
          key={innerProps.path}
          {...innerProps}
          element={
            useSuspense ? (
              <PublicSuspenseWrapper key={innerProps.path}>
                {element}
              </PublicSuspenseWrapper>
            ) : (
              element
            )
          }
        />
      ))}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
